import { ImageServiceType } from '@omni/kit/Types';
import Hoverable from '@omni/kit/components/Hoverable';
import KitFadeView from '@omni/kit/components/KitFadeView';
import usePlayerControls from '@omni/kit/components/KitPlayer/components/Controls/hooks/usePlayerControls';
import useLiveControls, {
  IS_WATCHING_LIVE_THRESHOLD,
} from '@omni/kit/components/KitPlayer/hooks/useLiveControls';
import { KitWavesLoadingIndicator } from '@omni/kit/components/KitWavesLoadingIndicator';
import { TargetProps } from '@omni/kit/components/Popover';
import ShareModal from '@omni/kit/components/ShareModal';
import GetSizeClassV2 from '@omni/kit/contexts/SizeClassContext/GetSizeClassV2';
import { SizeClassV2 } from '@omni/kit/contexts/SizeClassContext/types';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import buildShortLink from '@omni/kit/utilities/buildShortLink';
import { parseImageUrl } from '@omni/kit/utilities/utilities';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  Image,
  Platform,
  Pressable,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

import {
  checkIfColorWithinAcceptableThreshold,
  hexToRGBA,
} from '../../../../../../utilities/colorTools';
import { PlayerEvents } from '../../../../playbackTypes';
import PlayerPrompt from '../../../PlayerPrompt';
import BasicInfo from '../../components/BasicInfo';
import ControlsBackground from '../../components/ControlsBackground';
import LiveIndicator from '../../components/LiveStatus';
import LiveTag from '../../components/LiveTag';
import { PlayerControlsProps, PlayerControlsRef } from '../../types';
import { PlayerControlsDefinitions } from '../types';
import { defaultControls } from './DefaultControls.types';
import DismissControl from './components/DismissControl';
import FullscreenControl from './components/FullscreenControl';
import GiveButton from './components/GiveButton';
import PlayPauseControl from './components/PlayPauseControl';
import { PlayerButtonSizes } from './components/PlayerButton';
import PlayerTimeline from './components/PlayerTimeline/PlayerTimeline';
import PlayerTimestamps from './components/PlayerTimeline/PlayerTimestamps';
import ResumePlayControl from './components/ResumePlayControl/ResumePlayControl';
import SettingsControl from './components/SettingsControl/SettingsControl';
import { SettingsControlRef } from './components/SettingsControl/SettingsControl.types';
import ShareButton from './components/ShareButton';
import SkipBackwardControl from './components/SkipBackwardControl';
import SkipForwardControl from './components/SkipForwardControl';
import VolumeControl from './components/VolumeControl/VolumeControl';

/** Media progress has to be past 5 seconds before allowing to resume */
const BEGINNING_THRESHOLD = 5;
const ENDING_THRESHOLD = 1;
const LOGO_WATERMARK_SIZE = 30;
//The minimum screen height for the default volume slider to be displayed at its default height
const MIN_SCREEN_HEIGHT_FOR_DEFAULT_VOLUME_SLIDER = 250;
const DEFAULT_VOLUME_SLIDER_HEIGHT = 150;
const VOLUME_INCREMENT = 0.05; /** 0 ~ 1 */

const debug = require('debug')(
  'tca:KitPlayer:components:Controls:DefaultControls'
);

/**
 * DefaultControls component is responsible for rendering the player controls
 *
 * Notes:
 * - Should contain mostly presentational logic
 * - Business logic should be handled in usePlayerControls hook
 */
const DefaultControls = forwardRef<PlayerControlsRef, PlayerControlsProps>(
  (
    {
      analyticsFormat = 'video',
      branding,
      bufferedTimeSeconds = 0,
      containerRef,
      playerContainerDimensions = { width: 0, height: 0 },
      controlOverrides,
      data,
      featureGiving,
      featureInfo,
      featureShareable,
      hasAudio,
      hasVideo,
      isFullscreen = false,
      isLive,
      isLoading = false,
      isPlayerReady = false,
      isPlaying = false,
      isPrePlayPosterShowing = true,
      logoWatermark,
      mediaType,
      mediaUrl,
      playerPromptRef,
      playerTimelineRef,
      playerVideoRef,
      playId = '',
      previousTimestamp = 0 /** seconds */,
      totalDurationSeconds,

      onChangeMediaType,
      onDismiss,
      onPlaybackSpeedChange,
      onSeekStart,
      onSeekTo,
      onToggleFullscreen,
      onTogglePlay,
    },
    playerControlsRef
  ) => {
    const { t } = useTranslation('media');
    const controlsContainerRef = useRef<View>(null);
    const { width: playerContainerWidth, height: playerContainerHeight } =
      playerContainerDimensions;

    const sizeClass = GetSizeClassV2(playerContainerWidth);
    const isSmallScreen = sizeClass <= SizeClassV2.XS;
    const isTinyScreen = sizeClass === SizeClassV2.XXXS;

    const wrapperDefaultPadding = isSmallScreen ? Spacing.l : Spacing.xl;
    const wrapperTopPadding = isSmallScreen ? Spacing.m : Spacing.xl;

    const [isScreenfullEnabled, setIsScreenfullEnabled] = useState(false);
    const [isMenuOrVolumeOpen, setIsMenuOrVolumeOpen] =
      useState<boolean>(false);
    const [isTimelineInteracting, setIsTimelineInteracting] = useState(false);
    const timelineInteractionTimeoutRef =
      useRef<ReturnType<typeof setTimeout>>(undefined);

    const [wasMediaPlaying, setWasMediaPlaying] = useState(isPlaying);

    const [showResumeButton, setShowResumeButton] = useState<boolean>(false);
    const settingsControlRef = useRef<SettingsControlRef | null>(null);

    const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
    const shareModalRef = useRef<TargetProps>({
      width: new Animated.Value(0),
      height: new Animated.Value(0),
      left: new Animated.Value(0),
      top: new Animated.Value(0),
    }).current;

    const brandColor = useMemo(() => {
      if (branding?.brandColor) {
        const [r, g, b] = hexToRGBA(branding.brandColor);

        if (checkIfColorWithinAcceptableThreshold({ r, g, b })) {
          return branding.brandColor;
        }
      }

      return Colors.N0;
    }, [branding?.brandColor]);

    const controls: PlayerControlsDefinitions = useMemo(
      () => ({
        ...defaultControls,
        basicInfo: Platform.OS === 'web' && (featureInfo || isFullscreen),
        dismiss: Platform.OS !== 'web',
        fullscreen:
          Platform.OS === 'web'
            ? !isPrePlayPosterShowing && isScreenfullEnabled
            : !isPrePlayPosterShowing,
        timeline: Platform.OS === 'web' && !isPrePlayPosterShowing,
        volume: Platform.OS === 'web' && !isPrePlayPosterShowing,
        settings: Platform.OS === 'web' && !isPrePlayPosterShowing,
        legacyLiveChat: Platform.OS === 'web' && !isPrePlayPosterShowing,
        ...controlOverrides,
      }),
      [
        isScreenfullEnabled,
        featureInfo,
        controlOverrides,
        isPrePlayPosterShowing,
        isFullscreen,
      ]
    );

    const showTimeline = useMemo(() => {
      return (
        controls.timeline &&
        totalDurationSeconds &&
        Number.isFinite(totalDurationSeconds) &&
        totalDurationSeconds > 0
      );
    }, [controls.timeline, totalDurationSeconds]);

    const showSkip = useMemo(() => {
      if (!totalDurationSeconds) return false;

      if (totalDurationSeconds <= 0) return false;

      return Number.isFinite(totalDurationSeconds);
    }, [totalDurationSeconds]);

    const {
      isWatchingLive,
      setIsWatchingLive,
      goLive,
      calculateIsWatchingLive,
      setHasManuallySeekd,
    } = useLiveControls({
      playerVideoRef,
      isLive,
      totalDurationSeconds,
      currentTimeInSeconds:
        playerVideoRef?.current?.getCurrentTimeInSeconds() ?? 0,
      onSeekTo,
    });

    const detectIsLiveStateOnSkip = useCallback(
      (skipAmountInSeconds: number) => {
        const currentTimeInSeconds =
          playerVideoRef?.current?.getCurrentTimeInSeconds() ?? -1;
        const seekToSeconds = currentTimeInSeconds + skipAmountInSeconds;

        if (
          totalDurationSeconds &&
          totalDurationSeconds - seekToSeconds <= IS_WATCHING_LIVE_THRESHOLD
        ) {
          goLive();
        } else if (isLive && isWatchingLive) {
          setIsWatchingLive(false);
        }
      },
      [
        playerVideoRef,
        totalDurationSeconds,
        isLive,
        isWatchingLive,
        goLive,
        setIsWatchingLive,
      ]
    );

    const focusOnControlContainer = useCallback(() => {
      controlsContainerRef?.current?.focus();
    }, [controlsContainerRef]);

    const {
      areControlsVisible,
      isMuted,
      volume,

      handleGive,
      handleMutechange,
      handlePlaybackSpeedChange,
      handleResume,
      handleSelectQualityLevel,
      handleSelectSubtitleTrack,
      handleStartOver,
      handleContainerPress,
      handleToggleFullscreen,
      handleTogglePlay,
      handleVolumeChange,
      hideControls,
      sendAnalyticsPlayEvent,
      showControls,
      skipBackward,
      skipForward,
    } = usePlayerControls({
      analyticsFormat,
      appKey: data.appKey,
      mediaShortCode: data.mediaItemShortCode,
      controls,
      isLoading,
      isMenuOrVolumeOpen,
      isPlaying,
      isShareModalOpen,
      controlsContainerRef,
      playerControlsRef,
      playerVideoRef,
      playId,
      previousTimestamp,
      totalDurationSeconds,
      settingsControlRef,
      isPrePlayPosterShowing,

      onPlaybackSpeedChange,
      onSeekTo,
      onSeekStart,
      onToggleFullscreen,
      onTogglePlay,
      detectIsLiveStateOnSkip,
      setIsWatchingLive,
    });

    const CenterButton = useCallback(
      ({ previousTimestamp }: { previousTimestamp?: number }) => {
        const isSmall = sizeClass < SizeClassV2.XS;

        if (showResumeButton) {
          return (
            <ResumePlayControl
              isVisible={areControlsVisible}
              onPlay={handleStartOver}
              onResume={handleResume}
              progress={previousTimestamp || 0}
            />
          );
        }

        /**
         * Since KitLoadingIndicatorWaves sizes are md=48px and sm=20px
         * we need to adjust the margin gap to match the size of the loader
         * to avoid the controls from jumping when the loader is shown
         */
        const calculateMarginGap = () => {
          if (isSmall) {
            return isLoading ? Spacing.xl : Spacing.m;
          }

          return isLoading ? 32 : Spacing.xl;
        };

        const marginGap = calculateMarginGap();

        return (
          <>
            {controls.skipBackward && showSkip ? (
              <SkipBackwardControl
                isVisible={areControlsVisible}
                style={{ marginRight: marginGap }}
                onPressOut={focusOnControlContainer}
                onPress={skipBackward}
                size={
                  isSmall ? PlayerButtonSizes.small : PlayerButtonSizes.medium
                }
              />
            ) : null}
            {isLoading ? (
              <KitWavesLoadingIndicator
                color='white'
                size={isSmall ? 'sm' : 'md'}
              />
            ) : (
              <PlayPauseControl
                isVisible={areControlsVisible}
                isPlaying={isPlaying}
                onPressOut={focusOnControlContainer}
                onPress={handleTogglePlay}
                size={
                  isSmall ? PlayerButtonSizes.small : PlayerButtonSizes.medium
                }
              />
            )}
            {controls.skipForward && showSkip ? (
              <SkipForwardControl
                isVisible={areControlsVisible}
                style={{ marginLeft: marginGap }}
                onPressOut={focusOnControlContainer}
                onPress={skipForward}
                size={
                  isSmall ? PlayerButtonSizes.small : PlayerButtonSizes.medium
                }
              />
            ) : null}
          </>
        );
      },
      [
        areControlsVisible,
        controls.skipBackward,
        controls.skipForward,
        focusOnControlContainer,
        handleResume,
        handleStartOver,
        handleTogglePlay,
        isLoading,
        isPlaying,
        showResumeButton,
        showSkip,
        sizeClass,
        skipBackward,
        skipForward,
      ]
    );

    useEffect(() => {
      setIsScreenfullEnabled(
        playerVideoRef?.current?.hasFullScreenSupport() ?? false
      );
    }, [isPlayerReady, playerVideoRef]);

    /** TODO: move into usePlayerControls hook */
    const onDragStart = useCallback(() => {
      if (timelineInteractionTimeoutRef.current) {
        clearTimeout(timelineInteractionTimeoutRef.current);
      }

      onSeekStart?.();
      setWasMediaPlaying(isPlaying);
      setIsTimelineInteracting(true);
      if (isPlaying) {
        playerVideoRef?.current?.pause();
      }
    }, [isPlaying, onSeekStart, playerVideoRef]);

    /** TODO: move into usePlayerControls hook */
    const onDragEnd = useCallback(() => {
      if (wasMediaPlaying) playerVideoRef?.current?.play();

      if (timelineInteractionTimeoutRef.current) {
        clearTimeout(timelineInteractionTimeoutRef.current);
      }

      /**
       * Added the interaction timout to prevent handleContainerPress from being triggered
       * when the user is interacting with the timeline and releases click on the screen
       */
      timelineInteractionTimeoutRef.current = setTimeout(() => {
        setIsTimelineInteracting(false);
      }, 100);
    }, [playerVideoRef, wasMediaPlaying]);

    useEffect(() => {
      return () => {
        if (timelineInteractionTimeoutRef.current) {
          clearTimeout(timelineInteractionTimeoutRef.current);
        }
      };
    }, []);

    const shortLink = useMemo(
      () =>
        buildShortLink({
          shortCode: data?.mediaItemShortCode,
          customPath: data?.customPath,
        }),
      [data?.mediaItemShortCode, data?.customPath]
    );

    const otherControlsContainerStyle = useMemo(() => {
      if (isTinyScreen) {
        return {};
      }

      if (isSmallScreen) {
        return { justifyContent: 'space-between', width: '100%' };
      }

      return { gap: Spacing.xl };
    }, [isSmallScreen, isTinyScreen]);

    const parsedLogoWatermark = useMemo(() => {
      const url =
        parseImageUrl(
          logoWatermark || '',
          LOGO_WATERMARK_SIZE,
          LOGO_WATERMARK_SIZE,
          ImageServiceType.ImagePng
        ) || '';

      try {
        const imageUrl = new URL(url);

        imageUrl.searchParams.set('f', 'white');

        return imageUrl.toString();
      } catch (e) {
        debug('Invalid logo watermark url', url);
      }
    }, [logoWatermark]);

    const showLogoWatermark = useMemo(() => {
      if (!parsedLogoWatermark) return false;

      if (isPrePlayPosterShowing) return false;

      return !areControlsVisible;
    }, [areControlsVisible, isPrePlayPosterShowing, parsedLogoWatermark]);

    const updateShareModalCoord = useCallback(
      (
        _x: number,
        _y: number,
        width: number,
        height: number,
        pageX: number,
        pageY: number
      ) => {
        shareModalRef.width.setValue(width);
        shareModalRef.height.setValue(height);
        shareModalRef.left.setValue(pageX);
        shareModalRef.top.setValue(pageY);
      },
      [shareModalRef]
    );

    const onSendSharedAnalyticsEvent = useCallback(
      (shareTo: string) => {
        sendAnalyticsPlayEvent(PlayerEvents.share, shareTo);
      },
      [sendAnalyticsPlayEvent]
    );

    const showShareModal = useCallback((show: boolean) => {
      setIsShareModalOpen(show);
    }, []);

    const onOpenShareModal = useCallback(() => {
      window?.dispatchEvent(new Event('resize'));
      showShareModal(true);
      sendAnalyticsPlayEvent(PlayerEvents.share, 'share-modal');
    }, [showShareModal, sendAnalyticsPlayEvent]);

    const onCloseShareModal = useCallback(() => {
      showShareModal(false);
    }, [showShareModal]);

    const onToggleMenuOrVolumeOpen = useCallback((value: boolean) => {
      setIsMenuOrVolumeOpen(value);
    }, []);

    useEffect(() => {
      if (
        !isLive &&
        previousTimestamp &&
        totalDurationSeconds &&
        !isNaN(totalDurationSeconds)
      ) {
        const show =
          BEGINNING_THRESHOLD < previousTimestamp &&
          previousTimestamp < totalDurationSeconds - ENDING_THRESHOLD;
        setShowResumeButton(isPrePlayPosterShowing && show);
      }
    }, [
      isPrePlayPosterShowing,
      previousTimestamp,
      totalDurationSeconds,
      isLive,
    ]);

    useEffect(() => {
      if (Platform.OS !== 'web') return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (isPrePlayPosterShowing) return;

        if (!controls.hotkeys) return;

        switch (event.key) {
          case 'j':
          case 'ArrowLeft':
            showControls();
            skipBackward?.();
            event.preventDefault();
            break;
          case 'l':
          case 'ArrowRight':
            showControls();
            skipForward?.();
            event.preventDefault();
            break;
          case 'ArrowUp': {
            showControls();
            const newVolume = volume + VOLUME_INCREMENT;
            handleMutechange?.(newVolume <= 0);
            handleVolumeChange?.(newVolume);
            event.preventDefault();
            break;
          }
          case 'ArrowDown': {
            showControls();
            const newVolume = volume - VOLUME_INCREMENT;
            handleMutechange?.(newVolume <= 0);
            handleVolumeChange?.(newVolume);
            event.preventDefault();
            break;
          }
          case 'f':
            showControls();
            handleToggleFullscreen?.();
            event.preventDefault();
            break;
          case ' ':
          case 'k':
            showControls();
            handleTogglePlay?.();
            event.preventDefault();
            break;
          case 'm':
            showControls();
            handleMutechange?.(!isMuted);
            event.preventDefault();
            break;
          case 'c':
            showControls();
            settingsControlRef?.current?.toggleSubtitle();
            event.preventDefault();
            break;
          default:
            return; /** Key is not handled */
        }
      };

      const mediaElement = containerRef?.current as HTMLDivElement | undefined;

      if (!mediaElement) return;

      mediaElement.addEventListener('keydown', handleKeyDown);

      return () => {
        mediaElement.removeEventListener('keydown', handleKeyDown);
      };
    }, [
      containerRef,
      controls.hotkeys,
      handleMutechange,
      handleToggleFullscreen,
      handleTogglePlay,
      handleVolumeChange,
      isMuted,
      isPrePlayPosterShowing,
      playerVideoRef,
      settingsControlRef,
      showControls,
      skipBackward,
      skipForward,
      volume,
    ]);

    return (
      <>
        <Hoverable
          onHoverIn={() => {
            showControls();
          }}
          onHoverMove={showControls}
          onHoverOut={() => {
            if (
              !isMenuOrVolumeOpen &&
              !isLoading &&
              isPlaying &&
              !isShareModalOpen
            ) {
              hideControls();
            }
          }}
        >
          <Pressable
            ref={controlsContainerRef}
            focusable={false}
            isTVSelectable={false}
            style={[
              StyleSheet.absoluteFill,
              { outlineStyle: Platform.OS === 'web' ? 'none' : undefined },
              areControlsVisible
                ? styles.controlsContainerWithControlsVisible
                : styles.controlsContainerWithControlsHidden,
            ]}
            onPress={() => {
              // Only trigger handleContainerPress if the user is not interacting with the timeline
              if (!isMenuOrVolumeOpen && !isTimelineInteracting) {
                handleContainerPress();
              }
            }}
          >
            <View
              style={[
                styles.controlsContainer,
                {
                  paddingHorizontal: wrapperDefaultPadding,
                  paddingBottom: wrapperDefaultPadding,
                  paddingTop: controls.dismiss
                    ? wrapperTopPadding
                    : wrapperDefaultPadding,
                },
              ]}
            >
              <ControlsBackground
                isVisible={Boolean(
                  areControlsVisible && !isPrePlayPosterShowing
                )}
                type={controls.backgroundType}
              />

              <View style={styles.topControls}>
                <View style={styles.topLeftControls}>
                  {controls.dismiss ? (
                    <DismissControl
                      isVisible={areControlsVisible}
                      onPress={onDismiss}
                      style={{
                        margin: -16,
                      }}
                    />
                  ) : controls.basicInfo && !isPrePlayPosterShowing ? (
                    <BasicInfo
                      isVisible={areControlsVisible}
                      title={data.title}
                      subtitle={isSmallScreen ? '' : data.subtitle}
                    />
                  ) : null}
                </View>
                <View style={styles.topRightControls}>
                  {Platform.OS === 'web' &&
                  featureGiving &&
                  data.appKey &&
                  data.mediaItemShortCode &&
                  !isPrePlayPosterShowing &&
                  !isTinyScreen ? (
                    <GiveButton
                      isVisible={areControlsVisible}
                      onGive={handleGive}
                    />
                  ) : null}

                  {Platform.OS === 'web' &&
                  featureShareable &&
                  !isPrePlayPosterShowing &&
                  !isTinyScreen ? (
                    <ShareButton
                      isVisible={areControlsVisible}
                      openShareModal={onOpenShareModal}
                      updateShareModalCoord={updateShareModalCoord}
                    />
                  ) : null}
                </View>
              </View>

              {mediaUrl !== '' ? (
                <View style={styles.centerControls}>
                  <CenterButton previousTimestamp={previousTimestamp} />
                </View>
              ) : null}

              {/**
               * Only added this conditional to not affect LiveChat with the new
               * design changes until controls are flushed out
               */}
              {isLive && controls.legacyLiveChat ? (
                <View
                  style={[
                    styles.bottomControlsContainer,
                    { justifyContent: 'space-between' },
                  ]}
                >
                  {!isPrePlayPosterShowing ? (
                    <LiveIndicator isVisible={areControlsVisible} />
                  ) : null}

                  {controls.fullscreen && mediaUrl !== '' ? (
                    <View
                      style={
                        Platform.OS !== 'web'
                          ? { margin: -Spacing.xl }
                          : undefined
                      }
                    >
                      <FullscreenControl
                        isVisible={areControlsVisible}
                        isFullscreen={isFullscreen}
                        toggleFullscreen={handleToggleFullscreen}
                      />
                    </View>
                  ) : null}
                </View>
              ) : (
                <View
                  style={[
                    styles.bottomControlsContainer,
                    isSmallScreen &&
                      !isTinyScreen &&
                      styles.bottomControlsContainerSmall,
                  ]}
                >
                  <View
                    style={
                      isSmallScreen && !isTinyScreen
                        ? { width: '100%' }
                        : { flex: 1 }
                    }
                  >
                    {showTimeline ? (
                      <PlayerTimeline
                        ref={playerTimelineRef}
                        isVisible={areControlsVisible}
                        bufferedTimeSeconds={bufferedTimeSeconds}
                        totalDurationSeconds={totalDurationSeconds}
                        onSeeking={onSeekTo}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        currentTimebarStyle={{
                          backgroundColor: brandColor,
                        }}
                        playerVideoRef={playerVideoRef}
                        isLive={isLive}
                        isWatchingLive={isWatchingLive}
                        setIsWatchingLive={setIsWatchingLive}
                        calculateIsWatchingLive={calculateIsWatchingLive}
                        setHasManuallySeekd={setHasManuallySeekd}
                        goLive={goLive}
                      />
                    ) : null}
                  </View>

                  <View
                    style={[
                      styles.otherControlsContainer,
                      otherControlsContainerStyle as ViewStyle,
                    ]}
                  >
                    <View>
                      {controls?.timeline && !isTinyScreen ? (
                        <PlayerTimestamps
                          isVisible={areControlsVisible}
                          currentTime={
                            playerVideoRef?.current?.getCurrentTimeInSeconds() ??
                            0
                          }
                          totalDurationSeconds={totalDurationSeconds}
                          isLive={isLive}
                        />
                      ) : null}
                    </View>

                    <View style={styles.rightControls}>
                      {!isPrePlayPosterShowing && isLive ? (
                        <LiveTag
                          isVisible={areControlsVisible}
                          onPress={goLive}
                          isLive={isWatchingLive}
                          isClickable={true}
                          tooltipProps={{
                            label: t('skipToLive'),
                          }}
                        />
                      ) : null}

                      {controls.volume && !isTinyScreen ? (
                        <VolumeControl
                          isVisible={areControlsVisible}
                          isMuted={isMuted}
                          onUpdateMute={handleMutechange}
                          onUpdateVolume={handleVolumeChange}
                          onOpen={onToggleMenuOrVolumeOpen}
                          settingsControlRef={settingsControlRef}
                          sliderContainerStyle={{
                            height:
                              playerContainerHeight >
                              MIN_SCREEN_HEIGHT_FOR_DEFAULT_VOLUME_SLIDER
                                ? DEFAULT_VOLUME_SLIDER_HEIGHT
                                : Math.max(
                                    ((playerContainerHeight - Spacing.xxl) /
                                      MIN_SCREEN_HEIGHT_FOR_DEFAULT_VOLUME_SLIDER) *
                                      DEFAULT_VOLUME_SLIDER_HEIGHT,
                                    50
                                  ),
                          }}
                          sliderStyle={{
                            backgroundColor: brandColor,
                          }}
                          volume={volume}
                        />
                      ) : null}

                      {controls.settings && !isTinyScreen ? (
                        <SettingsControl
                          ref={settingsControlRef}
                          isPlayerReady={isPlayerReady}
                          isVisible={areControlsVisible}
                          hasAudio={hasAudio}
                          hasVideo={hasVideo}
                          mediaType={mediaType}
                          onSelectQualityLevel={handleSelectQualityLevel}
                          onSelectSubtitleTrack={handleSelectSubtitleTrack}
                          onOpen={onToggleMenuOrVolumeOpen}
                          onChangeMediaType={onChangeMediaType}
                          onPlaybackSpeedChange={handlePlaybackSpeedChange}
                          playerVideoRef={playerVideoRef}
                          playerContainerDimensions={playerContainerDimensions}
                        />
                      ) : null}

                      {controls.fullscreen && mediaUrl !== '' ? (
                        <FullscreenControl
                          isVisible={areControlsVisible}
                          isFullscreen={isFullscreen}
                          toggleFullscreen={handleToggleFullscreen}
                        />
                      ) : null}
                    </View>
                  </View>
                </View>
              )}

              <KitFadeView show={showLogoWatermark} style={[styles.watermark]}>
                <Image
                  source={{
                    uri: parsedLogoWatermark,
                    width: LOGO_WATERMARK_SIZE,
                    height: LOGO_WATERMARK_SIZE,
                  }}
                  resizeMode='cover'
                />
              </KitFadeView>
            </View>
          </Pressable>
        </Hoverable>

        {data.appKey && data.mediaItemShortCode ? (
          <PlayerPrompt
            appKey={data.appKey}
            mediaItemShortCode={data.mediaItemShortCode}
            isPlaying={isPlaying}
            isLoading={isLoading}
            playerVideoRef={playerVideoRef}
            ref={playerPromptRef}
          />
        ) : null}

        <ShareModal
          isOpen={isShareModalOpen}
          anchorBottom={isSmallScreen}
          onClose={onCloseShareModal}
          shortLink={shortLink}
          targetRef={shareModalRef}
          onSendAnalytics={onSendSharedAnalyticsEvent}
        />
      </>
    );
  }
);

const CENTER_FLEX: ViewStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const styles = StyleSheet.create({
  controlsContainer: {
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    position: 'relative',
    width: '100%',
  },
  controlsContainerWithControlsVisible: { cursor: 'initial' } as any,
  controlsContainerWithControlsHidden: { cursor: 'none' } as any,
  topControls: {
    display: 'flex',
    flexDirection: 'row',
    gap: Spacing.s,
    justifyContent: 'space-between',
    marginBottom: 'auto',
    width: '100%',
  },
  topLeftControls: {
    flex: 1,
  },
  topRightControls: {
    display: 'flex',
    flexDirection: 'row',
    gap: Spacing.xs,
    justifyContent: 'flex-end',
    margin: -Spacing.m,
  },
  centerControls: {
    ...CENTER_FLEX,
    flexDirection: 'row',
    alignSelf: 'center',
    position: 'absolute',
  },
  bottomControlsContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    marginTop: 'auto',
    gap: Spacing.xl,
  },
  bottomControlsContainerSmall: {
    flexDirection: 'column',
    gap: Spacing.l,
  },
  timelineContainer: {
    flex: 1,
  },
  otherControlsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  leftControls: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  rightControls: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    margin: -Spacing.m,
  },
  watermark: {
    position: 'absolute',
    bottom: Spacing.xl,
    right: Spacing.xl,
    zIndex: 1,
    opacity: 0.75,
  },
});

export default DefaultControls;
